import { useEffect, useState } from 'react'
import type { CSSProperties, MouseEvent } from 'react'
import { VoxoraLockup } from './components/Brand'
import HeaderNav from './components/HeaderNav'
import HeaderMobileMenu from './components/HeaderMobileMenu'
import HomeUploader from './components/HomeUploader'
import Footer from './components/Footer'
import HeroQuoteVisual from './components/HeroQuoteVisual'
import { ESTIMATE_PATH, navigate } from './lib/router'
import { useScrollScene } from './hooks/useScrollScene'
import { MATERIALS, QUALITIES, byLayerHeightDesc, colorsFor } from './lib/catalog'
import type { ColorOption } from './lib/catalog'
import { FALLBACK_PARAMS } from './lib/estimate'
import { SITE } from './lib/site'

/*
  Az arlista a becslesi parameterekbol jon, hogy EGY helyen alljon.
  Ha a `estimation_params` valtozik, a marketingszoveg is vele valtozik -
  kulonben az oldalon mas ar allna, mint amit a kalkulator szamol.
*/
const PRICING = {
  materialPerGram: FALLBACK_PARAMS.pricing.material_per_gram,
  energyPerHour: FALLBACK_PARAMS.pricing.energy_per_hour,
  minimumOrder: FALLBACK_PARAMS.pricing.minimum_order,
  expressPct: FALLBACK_PARAMS.express.pct,
  expressMin: FALLBACK_PARAMS.express.min_huf
}
import { huf } from './lib/format'

/*
  A folyamat lepesei. Minden lepesnel megmondjuk, mi tortenik ES miert,
  mert ez a resz a latogato vegigvezetese, nem marketingszoveg.
*/
const FLOW = [
  {
    title: 'Feltöltöd a kész 3D fájlt',
    body: [
      'STL, OBJ vagy 3MF formátumot fogadunk; több alkatrészt külön fájlban vagy egy ZIP-ben is feltölthetsz.',
      'A böngésződ helyben beolvassa a geometriát, ellenőrzi a befoglaló méretet és kiszámítja az anyagigényt. A fájl csak az ajánlatkérés elküldésekor kerül hozzánk.'
    ],
    specs: [['Formátum', 'STL / OBJ / 3MF'], ['Max. méret', '260 × 260 × 260 mm'], ['Gyártási díj', 'azonnal']]
  },
  {
    title: 'Megadod a gyártási célt',
    body: [
      'A modell már kész; nekünk azt írd meg, hogyan fog dolgozni a nyomat: beltérre vagy kültérre kerül, kap-e terhelést, és van-e kritikus illeszkedő mérete.',
      'Ha már tudod az anyagot és a profilt, válaszd ki. Ha csak a felhasználási célt tudod biztosan, az Általános beállítás jó kiindulópont; az ajánlat előtt ellenőrizzük.'
    ],
    specs: [['PETG', 'tartós'], ['PLA', 'részletes'], ['Anyagválasztás', 'segítünk']]
  },
  {
    title: 'Kiválasztod a nyomtatási profilt',
    body: [
      'Gyors prototípushoz 0,30 mm, általános használatra 0,20 mm, finom felülethez 0,12 vagy 0,08 mm rétegmagasságot választhatsz.',
      'Minden profilnál látod a várható felületi eredményt és felhasználást. A gyártási díj minden beállításmódosítás után azonnal frissül.'
    ],
    specs: [['Réteg', '0,08–0,30 mm'], ['Profilok', 'érthetően'], ['Ár', 'azonnal frissül']]
  },
  {
    title: 'Gyárthatóságot ellenőrzünk',
    body: [
      /*
        Itt korabban az allt, hogy "atnezzuk" es "ha kockazatot latunk" - ez
        emberi szemlet igert. Az ellenorzes AUTOMATIKUS (lasd ASZF 4. pont),
        es ezt ki is mondjuk. A "A geometriat nem irjuk at" mondat SZO SZERINT
        marad: a `serviceScopeCopy` teszt erre a kifejezesre illeszkedik.

        A FALVASTAGSAG KIKERULT A FELSOROLASBOL - nem rovidites, hanem
        javitas: az igeret NEM VOLT IGAZ. Egy haromszoghalobol a
        falvastagsag voxelezes vagy sugarkovetes nelkul nem merheto
        megbizhatoan, ezert a `printability/check.ts` szandekosan NEM is
        vizsgalja (lasd az ottani indoklast: egy bordazott alkatreszre vagy
        egy vekony feliratra minden olcso kozelites hamisan riasztana).

        Ami a listaban maradt, azt a bongeszo MOSTANTOL tenylegesen meg is
        nezi a feltoltes utan, nem csak a bekuldes utani gyartasi ellenorzes.
      */
      'A méretet, a zártságot, a tájolást és a támaszigényt minden fájlon automatikusan ellenőrizzük. A geometriát nem írjuk át: ha az ellenőrzés kockázatot jelez, pontosan megírjuk, melyik rész érintett.',
      'Négy órán belül visszajelzünk a gyárthatóságról és a várható határidőről. Ha új export szükséges, a frissített fájllal folytatjuk; a gyártás csak a jóváhagyásod után indul.'
    ],
    specs: [['Válasz', '4 órán belül'], ['Ellenőrzés', 'automatikus, minden modellnél'], ['Gyártás', 'jóváhagyás után']]
  },
  {
    title: 'Kinyomtatjuk és átadjuk',
    body: [
      'A jóváhagyás után legyártjuk, ellenőrizzük és biztonságosan becsomagoljuk a rendelést. Hibás nyomatot nem adunk át, az újranyomtatás költsége minket terhel.',
      'A kész darabot átveheted személyesen, kérheted csomagpontra vagy házhoz szállítással.'
    ],
    specs: [['Ellenőrzés', 'átadás előtt'], ['Számla', 'elektronikus'], ['Átvétel', '3 lehetőség']]
  }
]

const FAQ = [
  {
    id: 'nem-ertek-hozza',
    q: 'Megvan a 3D fájlom, de nem értek a nyomtatási beállításokhoz. Ez baj?',
    a: 'Nem. Te hozod a kész modellt és tudod, mire használod majd; mi a gyártási oldalt visszük. Ellenőrizzük az anyagot, a rétegprofilt, a tájolást és a támaszigényt. Ha a választott beállítás nem illik a célhoz, még nyomtatás előtt szólunk, és érthetően leírjuk, mit javaslunk.'
  },
  {
    id: 'fajl-allapota',
    q: 'Milyen állapotban legyen a 3D fájl?',
    a: 'Nyomtatásra kész STL, OBJ vagy 3MF fájlt várunk, valós méretben exportálva. A geometriát gyártás előtt automatikusan ellenőrizzük, és jelezzük a kockázatos részeket. Ha a fájl módosításra szorul, pontos visszajelzést kapsz, majd a frissített exportot újra feltöltheted.'
  },
  {
    id: 'ar',
    q: 'Tényleg ennyi lesz? Nem jön még hozzá valami?',
    a: `Az ár két tételből áll: ${PRICING.materialPerGram.toString().replace('.', ',')} Ft minden felhasznált grammért és ${PRICING.energyPerHour} Ft minden nyomtatási óráért, percre pontosan elszámolva. Nincs külön előkészítési vagy színfelár. A minimum rendelési érték ${huf(PRICING.minimumOrder)}. A kalkulátor a kiválasztott beállításokhoz tartozó gyártási díjat mutatja; a szállítás díja az átvételi mód kiválasztásakor adódik hozzá. Ha Expressz gyártást kérsz, arra a gyártási díj 30%-a, de legalább ${huf(3000)} felár jön rá. Alanyi adómentes vállalkozásként ÁFÁ-t nem számítunk fel.`
  },
  {
    id: 'illeszkedes',
    q: 'Biztosan passzolni fog?',
    a: 'A 3D nyomtatás pontos, de az illesztési hézagot a modellben kell kialakítani: már néhány tizedmilliméter is számíthat. Írd meg, melyik méret kritikus és mihez kapcsolódik. Kockázatos illeszkedésnél először próbadarabot javaslunk, így még a teljes rendelés előtt ellenőrizheted a méretet.'
  },
  {
    id: 'felulet',
    q: 'Olyan sima lesz, mint egy bolti műanyag tárgy?',
    a: 'Nem egészen. Az FDM nyomtatás rétegenként építkezik, ezért közelről finom rétegvonalak látszanak rajta. A részletes profil sokat javít a felületen, de nem lesz fröccsöntött hatású. Inkább előre mutatjuk meg, mire számíts, mint hogy egy túl szép ígéret érkezzen meg dobozban.'
  },
  {
    id: 'hatarido',
    q: 'Mennyi idő alatt lesz kész?',
    a: 'A nyomtatás gyakran órákig tart, egy nagyobb vagy részletes darab akár egy teljes napig is készülhet. Ehhez jön az előkészítés, az ellenőrzés és az aktuális várólista. A vállalható határidőt az ajánlatban írjuk meg – így nem egy optimista dátumot kapsz, hanem olyat, amit tartani is tudunk.'
  },
  {
    id: 'vizallo',
    q: 'Tényleg vízálló lesz?',
    a: 'Kaspóhoz vagy cseppfogó tálcához tudunk vízzárásra hangolt PETG-profilt használni. Nyomás alatt, tartós vízben vagy értékes felület fölött azonban az FDM nyomat önmagában nem tekinthető garantáltan vízállónak. Mondd el a felhasználást, és nem hagyjuk, hogy egy kaspóból véletlenül beltéri szökőkút legyen.'
  },
  {
    id: 'hiba',
    q: 'Mi van, ha elromlik nyomtatás közben?',
    a: 'Ha a nyomtatás hibázik, újrakezdjük, és ennek nincs pluszköltsége számodra. Ha maga a modell kockázatos – például túl vékony a fala, rossz a mérete vagy alátámasztás nélkül nem gyártható –, azt az ajánlatban jelezzük. A problémákat előbb szeretjük megtalálni, mint a csomagban.'
  },
  {
    /*
      Ez eddig CSAK az ASZF-ben es a Fogyasztoi tajekoztatoban szerepelt. Az
      elallas hianya ugyanabbol az okbol kovetkezik, mint az elore fizetes -
      es azt az okot a latogato elfogadja. Elhallgatva viszont a fizetes
      elotti pillanatban eri, ami a lehető legrosszabb hely.
    */
    id: 'elallas',
    q: 'Meggondolhatom magam, miután elküldtem az ajánlatkérést?',
    a: 'A jóváhagyásig igen. Az ajánlatkérés nem megrendelés, nem jár fizetési kötelezettséggel, és a gyártás csak a jóváhagyásod után indul. Utána viszont nem: a termék kifejezetten a te fájlodból, a te beállításaiddal készül, ezért a 45/2014. (II. 26.) Korm. rendelet 29. § (1) bekezdés c) pontja alapján nem illet meg a 14 napos, indokolás nélküli elállási jog. Ez ugyanaz az ok, amiért a gyártás előtt kérünk fizetést: a legyártott darabot másnak nem tudjuk eladni. A hibás teljesítésre vonatkozó kellékszavatossági jogaid ettől függetlenül megilletnek — a részleteket a Fogyasztói tájékoztatóban írtuk le.'
  },
  {
    id: 'korlatok',
    q: 'Van, amire nem jó a 3D nyomtatás?',
    a: 'Igen, és ezt fontos kimondani. Élelmiszerrel érintkező, nagy hőnek vagy nyomásnak kitett, illetve biztonságkritikus alkatrésznél külön egyeztetés kell, és lehet, hogy más gyártási módot javaslunk. Ha egy darab meghibásodása sérülést vagy komoly kárt okozhat, ezt mindenképp írd meg nekünk.'
  },
  {
    id: 'meret',
    q: 'Mekkora a legnagyobb darab?',
    a: 'Egy darabban legfeljebb 260 × 260 × 260 mm. Ha a tárgy ennél nagyobb, több, összeilleszthető 3D fájlra bontva töltsd fel. A kalkulátor minden részt külön ellenőriz; túlméretes fájlnál még az ajánlatkérés előtt egyértelmű jelzést kapsz.'
  }
]

/*
  A GYARTASI PROFILOK a fooldali skalan: a PETG-profilok, a legvastagabb
  retegtol a legvekonyabbig. A PLA ugyanezeket a retegmagassagokat kinalja,
  ezert kulon sor nem kell hozza.
*/
const SCALE_PROFILES = byLayerHeightDesc(QUALITIES.filter((q) => q.material === 'PETG'))

const comma = (value: number): string => String(value).replace('.', ',')

/** Retegmagassag mindig ket tizedessel: „0,30", nem „0,3" - ahogy a szoveg is irja. */
const layerMm = (value: number): string => value.toFixed(2).replace('.', ',')

/*
  ────────────────────────────────────────────────────────────────────
  A FILAMENTKOTEG: AZ ANYAGKARTYA TETEJEN ALLO SZINSAV.

  Nem dekoracio, hanem a szinvalasztek elso olvasata: minden sav egy
  elerheto szin, EGYMASRA RAKVA, ahogy a nyomat retegei. Ket szinnel (PLA)
  a minta ismetlodik, hogy a sav ugyanolyan suru maradjon, mint a hat
  szines PETG-e - kulonben a ket kartya teteje mas ritmusban csikozna.
  ────────────────────────────────────────────────────────────────────
*/
function filamentStack(colors: ColorOption[]): string {
  if (!colors.length) return 'none'
  const bands: string[] = []
  while (bands.length < 6) bands.push(...colors.map((c) => c.hex))
  const step = 100 / bands.length
  return `linear-gradient(180deg, ${bands
    .map((hex, i) => `${hex} ${(i * step).toFixed(3)}% ${((i + 1) * step).toFixed(3)}%`)
    .join(', ')})`
}

function Header(): JSX.Element {
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const onScroll = (): void => setStuck(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header className={`header ${stuck ? 'header--stuck' : ''}`}>
      <div className="shell header__inner">
        <a href="#top" aria-label="VOXORA, vissza a tetejére">
          <VoxoraLockup size={20} tone="light" />
        </a>
        <HeaderNav />
        {/*
          Nagyon keskeny kijelzon a felirat helyett ikon all (a szoveg
          `display: none`), de az `aria-label` miatt a kepernyoolvaso ugyanazt
          mondja.
        */}
        <a
          className="btn btn--primary header__cta"
          href={ESTIMATE_PATH}
          onClick={goEstimate}
          aria-label="Árat kérek"
        >
          <span className="header__nav-icon header__nav-icon--price" aria-hidden="true">
            <span className="header__nav-icon-shape" />
          </span>
          <span className="header__cta-text">Árat kérek</span>
        </a>
        <HeaderMobileMenu />
      </div>
    </header>
  )
}

/** A `/arbecsles` oldalra visz - de a Ctrl/Cmd-kattintast a bongeszore hagyja. */
function goEstimate(event: MouseEvent<HTMLAnchorElement>): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
  event.preventDefault()
  navigate(ESTIMATE_PATH)
}

export default function App(): JSX.Element {
  useScrollScene()
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  return (
    <>
      <Header />

      <main id="top">
        {/*
          ============ 1. hero: a fo muvelet, es mellette a bizonyitek ============

          A bal oszlop EGY dolgot ker: a fajlt. A jobb oszlop megmutatja, mi
          lesz belole - egy retegrol retegre felepulo targyat, es melle a
          jegyet, amin latszik, hogyan jon ki az ar. Az ar tehat mar az elso
          kepernyon ellenorizheto, nem a 12. kepernyon.
        */}
        <section className="hero">
          <div className="shell hero__grid">
            <div className="hero__copy">
              <p className="hero__kicker">
                <span className="hero__kicker-dot" aria-hidden="true" />
                Azonnali ár · regisztráció nélkül
              </p>
              <h1 className="hero__title">
                <span>A fájl megvan.</span>{' '}
                <em>Legyen belőle tárgy.</em>
              </h1>
              {/*
                A bevezeto nem ker olyan tudast, ami a latogatonak nincs: nem
                kell ertenie a nyomtatashoz. A GYIK elso kerdese ugyanezt mondja.
              */}
              <p className="lede hero__lede">
                Töltsd fel az STL, OBJ vagy 3MF fájlt, és rögtön látod az árát.
                Nem kell értened a nyomtatáshoz: a gyárthatóságot minden fájlnál
                automatikusan ellenőrizzük, és csak a jóváhagyásod után gyártunk.
              </p>

              {/*
                A `#rendeles` horgony IDE kerult. A regi fooldalon ez a feltolto
                szakasza volt, es kulso hivatkozasok (hirdetes, level) is
                mutathatnak ra: most is a feltoltohoz visznek.
              */}
              <div className="hero__upload" id="rendeles">
                <HomeUploader />
              </div>
            </div>

            <figure
              className="hero__frame"
              aria-label="Példa: egy váza gyártási díja a felhasznált anyagból és a nyomtatási időből"
            >
              <HeroQuoteVisual />
            </figure>
          </div>

          {/* ============ bizalmi sav: negy teny, egy sorban ============ */}
          <div className="shell">
            <dl className="ledger">
              <div className="ledger__cell">
                <dt className="tele">Árkalkuláció</dt>
                <dd className="ledger__value"><em>azonnal</em>, regisztráció nélkül</dd>
              </div>
              <div className="ledger__cell">
                <dt className="tele">Műszaki ellenőrzés</dt>
                <dd className="ledger__value"><em>minden</em> 3D fájlnál</dd>
              </div>
              {/*
                A sorrend a lenyeg: elobb jovahagyod, aztan fizetsz, es csak
                utana gyartunk.
              */}
              <div className="ledger__cell">
                <dt className="tele">Fizetés</dt>
                <dd className="ledger__value">jóváhagyás után, <em>gyártás</em> előtt</dd>
              </div>
              <div className="ledger__cell">
                <dt className="tele">Válaszidő</dt>
                <dd className="ledger__value"><em>4</em> órán belül</dd>
              </div>
            </dl>
          </div>
        </section>

        {/* ============ 2. folyamat: tapado cim, egymas alatti lepesek ============ */}
        <section className="section flow-section" id="folyamat">
          <div className="shell flow-layout">
            <header className="flow-layout__intro">
              <p className="eyebrow reveal">Hogyan működik</p>
              <h2 className="reveal">A fájltól a kész darabig</h2>
              <p className="lede flow__intro reveal">
                Ott vesszük át a folyamatot, ahol már megvan a kész 3D fájl. Öt
                átlátható lépésben látod, hogyan lesz belőle ellenőrzött, legyártott
                darab — és mikor van szükség a döntésedre.
              </p>
              <a
                className="btn btn--primary btn--lg reveal"
                href={ESTIMATE_PATH}
                onClick={goEstimate}
              >
                Kiszámolom az árat
              </a>
            </header>

            <ol className="flow">
              {FLOW.map((s, i) => (
                <li className="flow__row reveal" key={s.title}>
                  <span className="flow__no" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flow__copy">
                    <h3 className="flow__title">{s.title}</h3>
                    <div className="flow__body-stack">
                      {s.body.map((paragraph) => (
                        <p className="flow__body" key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                  <dl className="flow__aside">
                    {s.specs.map(([k, v]) => (
                      <div className="flow__spec" key={k}>
                        <dt>{k}</dt>
                        <dd>{v}</dd>
                      </div>
                    ))}
                  </dl>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============ 3. anyagok: ket kartya, egymas mellett ============ */}
        <section className="section materials" id="anyagok">
          <div className="shell">
            <header className="section-head">
              <p className="eyebrow reveal">Anyagok</p>
              <h2 className="reveal">A modelledhez illő anyag</h2>
              <p className="lede reveal">
                Te a modell célját ismered, mi a PLA és a PETG viselkedését. Mondd meg,
                hogy a nyomat terhelést kap, kültérre kerül vagy inkább részletgazdag
                felület kell — a választást gyártás előtt ellenőrizzük.
              </p>
            </header>

            <div className="versus">
              {MATERIALS.map((m) => {
                const colors = colorsFor(m.id)
                return (
                  <article className="versus__col reveal" key={m.id}>
                    <div
                      className="versus__swatch"
                      aria-hidden="true"
                      style={{ backgroundImage: filamentStack(colors) }}
                    />
                    <header className="versus__head">
                      <h3 className="versus__name">{m.label}</h3>
                      <span className="versus__density">{comma(m.density)} g/cm³</span>
                    </header>
                    <p className="versus__tag">{m.tagline}</p>

                    <div className="versus__lists">
                      <div>
                        <p className="versus__label">Erős oldala</p>
                        <ul className="versus__list">
                          {m.strengths.map((s) => <li key={s}>{s}</li>)}
                        </ul>
                      </div>
                      <div>
                        <p className="versus__label">Mire figyelj</p>
                        <ul className="versus__list versus__list--limit">
                          {m.limits.map((s) => <li key={s}>{s}</li>)}
                        </ul>
                      </div>
                    </div>

                    <div className="chips" aria-label={`${m.label} színek`}>
                      {colors.map((c) => (
                        <span className="chip-color" key={c.id}>
                          <span style={{ background: c.hex }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        {/* ============ 4. finomsag: retegfal-skala ============ */}
        <section className="section" id="finomsag">
          <div className="shell">
            <header className="section-head">
              <p className="eyebrow reveal">Felület és részletesség</p>
              <h2 className="reveal">Rétegprofil a felhasználáshoz</h2>
              <p className="lede reveal">
                0,30 mm gyors prototípushoz, 0,20 mm általános használatra, 0,12 vagy
                0,08 mm finom részletekhez. A profiloknál rögtön látod, milyen felületre,
                gyártási időre és árra számíthatsz.
              </p>
            </header>

            <div className="scale reveal">
              {SCALE_PROFILES.map((q) => (
                <div className="scale__step" key={q.id}>
                  {/*
                    A retegvastagsag vizualis megfeleloje: vekonyabb reteg =
                    surubb csikozas, kb. negyvenszeres nagyitasban. Nem
                    dekoracio, hanem a szam masodik olvasata azoknak, akik nem
                    szamokban gondolkodnak.
                  */}
                  <span
                    className="scale__bars"
                    aria-hidden="true"
                    style={{ '--layer-px': `${Math.max(3, q.layerHeight * 40)}px` } as CSSProperties}
                  />
                  <span className="scale__mm">{layerMm(q.layerHeight)}</span>
                  <span className="tele">mm réteg</span>
                  <span className="scale__name">{q.label}</span>
                  <span className="scale__use">{q.bestFor}</span>
                </div>
              ))}
            </div>
            <p className="scale__caption">
              A csíkok a rétegeket kb. negyvenszeres nagyításban mutatják.
            </p>
          </div>
        </section>

        {/* ============ 5. arak: a keplet, sotet feluleten ============ */}
        <section className="section pricing" id="arak">
          <div className="shell">
            <header className="section-head">
              <p className="eyebrow reveal">Átlátható árazás</p>
              <h2 className="reveal">Az ár nem zsákbamacska</h2>
              <p className="lede reveal">
                Két tétel adja a gyártási díjat: a felhasznált anyag és a nyomtatási
                idő. Mindkettőt a kalkulátor számolja ki a modelledből, grammra és
                percre pontosan.
              </p>
            </header>

            <div className="formula reveal" role="group" aria-label="A gyártási díj képlete">
              <div className="slab">
                <span className="slab__label">Anyag</span>
                <span className="slab__num">
                  {comma(PRICING.materialPerGram)}<sub>Ft/g</sub>
                </span>
                <span className="slab__note">A ténylegesen felhasznált filament, a támaszokkal együtt.</span>
              </div>
              <span className="formula__op" aria-hidden="true">+</span>
              <div className="slab">
                <span className="slab__label">Nyomtatási idő</span>
                <span className="slab__num">{PRICING.energyPerHour}<sub>Ft/óra</sub></span>
                <span className="slab__note">
                  Percre pontosan számolva. Egy 18 perces nyomtatásért nem kérünk egy teljes órát.
                </span>
              </div>
              <span className="formula__op" aria-hidden="true">=</span>
              <div className="slab slab--result">
                <span className="slab__label">Gyártási díj</span>
                <span className="slab__num slab__num--word">azonnal látod</span>
                <span className="slab__note">
                  A feltöltés után a kalkulátor kiszámolja — várakozás és regisztráció nélkül.
                </span>
              </div>
            </div>

            <div className="slabs reveal">
              <div className="slab">
                <span className="slab__num">
                  {PRICING.minimumOrder.toLocaleString('hu-HU')}<sub>Ft</sub>
                </span>
                <span className="slab__label">Rendelési minimum</span>
                <span className="slab__note">
                  A teljes rendelésre, nem darabonként. Több apró darab összeadódik.
                </span>
              </div>
              {/*
                A felirat nem „Felarak": a `0 Ft` csak az elokeszitesi es a
                szinfelarra igaz. Expressz gyartasnal VAN felar - ezert az
                mellette, kulon cellaban, a sajat szamaval all, nem aprobetuben.
              */}
              <div className="slab">
                <span className="slab__num">0<sub>Ft</sub></span>
                <span className="slab__label">Előkészítés és szín</span>
                <span className="slab__note">
                  Nincs külön előkészítési vagy színfelár. ÁFÁ-t sem számítunk fel.
                </span>
              </div>
              <div className="slab">
                <span className="slab__num">
                  +{Math.round(PRICING.expressPct * 100)}<sub>%</sub>
                </span>
                <span className="slab__label">Expressz gyártás</span>
                <span className="slab__note">
                  Csak ha kéred: a gyártási díj {Math.round(PRICING.expressPct * 100)}%-a,
                  de legalább {huf(PRICING.expressMin)}.
                </span>
              </div>
            </div>

            <p className="note-box reveal">
              Az oldalon látható összeg a kiválasztott beállításokhoz tartozó
              <strong> gyártási díj</strong>. A szállítás díja az átvételi mód
              kiválasztásakor adódik hozzá. A 3D fájlt a gyártás előtt automatikusan
              ellenőrizzük, és csak a jóváhagyásod után kezdünk nyomtatni.
            </p>
          </div>
        </section>

        {/* ============ 6. GYIK: cim balra, harmonika jobbra ============ */}
        <section className="section" id="gyik">
          <div className="shell faq-layout">
            <header className="faq-layout__intro">
              <p className="eyebrow reveal">Gyakori kérdések</p>
              <h2 className="reveal">Teljesen jogos kérdések</h2>
              <p className="lede reveal">
                Ami egy első 3D nyomtatás előtt mindenkiben felmerül — röviden,
                kertelés nélkül.
              </p>
              {(SITE.email || SITE.phone) && (
                <div className="faq-layout__contact reveal">
                  <span className="tele">Nem találod a választ?</span>
                  {SITE.email && (
                    <a className="link" href={`mailto:${SITE.email}`}>{SITE.email}</a>
                  )}
                  {SITE.phone && (
                    <a className="link" href={`tel:${SITE.phone.replace(/\s/g, '')}`}>{SITE.phone}</a>
                  )}
                </div>
              )}
            </header>

            <div className="faq">
              {FAQ.map((item, i) => (
                <div className="faq__item reveal" id={item.id} key={item.q} data-open={openFaq === i}>
                  <button
                    className="faq__q"
                    aria-expanded={openFaq === i}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="faq__idx">{String(i + 1).padStart(2, '0')}</span>
                    <span>{item.q}</span>
                    <span className="faq__sign" aria-hidden="true" />
                  </button>
                  <div className="faq__a">
                    <div><p>{item.a}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}
